import React, { useState, useEffect, ReactElement, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Params, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';

import { Member, Poll, PollOption, Post, Reaction } from '../../../../types/entity.types';
import { RootState } from '../../../../types/state/root.types';
import { MemberState } from '../../../../types/state/member.types';
import { TribeState } from '../../../../types/state/tribe.types';
import { SystemState } from '../../../../types/state/system.types';

import { generateUUID } from '../../../../utils/util';

import { addReaction, removeReaction } from '../../../../slices/programme/tribe/tribe.slice';

import {
  requestCreateReaction,
  requestDeleteReaction,
  requestSwitchReaction
} from '../../../../api/reaction/reaction.actions';
import { requestViewPollResults } from '../../../../api/poll/poll.actions';

import TribePoll from '../../../../components/screens/programme/tribe/tribe-poll/TribePoll';

type Props = {
  post: Post;
};

const TribePollContainer = ({ post }: Props): ReactElement => {
  const [poll, setPoll] = useState<Poll | undefined>(undefined);

  const { reactions, pollResults } = useSelector(({ tribe }: RootState): TribeState => tribe);
  const { members } = useSelector(({ member }: RootState): MemberState => member);
  const { user } = useSelector(({ system }: RootState): SystemState => system);

  const dispatch: Dispatch = useDispatch();

  const { programmeExternalReference }: Readonly<Params> = useParams();

  useEffect((): void => {
    if (!post.question || !post.options) return;

    const filteredReactions: Reaction[] = reactions.filter(
      (reaction: Reaction): boolean => reaction.postExternalReference === post.externalReference
    );

    const poll: Poll = {
      question: post.question as string,
      options: post.options.map((option: string): PollOption => {
        const votes: number =
          post.reactionCount?.find((reaction: Reaction): boolean => reaction.text === option)?.count || 0;
        const voted: boolean = filteredReactions.some((reaction: Reaction): boolean => reaction.reaction === option);

        return { id: generateUUID(), value: option, votes, voted };
      }),
      allowMultipleSelection: post.allowMultipleSelection
    };

    setPoll(poll);
  }, [post, reactions]);

  if (!poll) return <Fragment />;

  const postExternalReference: string = post.externalReference as string;

  const createReaction = (reaction: string): void => {
    dispatch(addReaction({ postExternalReference, reaction }));

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to reaction actions
      requestCreateReaction({
        input: {
          postExternalReference,
          programmeExternalReference,
          reaction
        },
        onError: (): void => {
          dispatch(removeReaction({ postExternalReference, reaction }));
        }
      })
    );
  };

  const deleteReaction = (reaction: string): void => {
    dispatch(removeReaction({ postExternalReference, reaction }));

    const member: Member | undefined = members.find((member: Member): boolean => member.username === user?.username);

    if (!member) return;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to reaction actions
      requestDeleteReaction({
        memberExternalReference: member.memberExternalReference,
        postExternalReference,
        reaction,
        onError: (): void => {
          dispatch(addReaction({ postExternalReference, reaction }));
        }
      })
    );
  };

  const switchReaction = (oldReaction: string, newReaction: string): void => {
    const member: Member | undefined = members.find((member: Member): boolean => member.username === user?.username);

    if (!member) return;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to reaction actions
      requestSwitchReaction({
        memberExternalReference: member.memberExternalReference,
        postExternalReference,
        programmeExternalReference,
        oldReaction,
        newReaction
      })
    );
  };

  const retrieveResults = (onSuccess: () => void): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to poll actions
      requestViewPollResults({
        pollExternalReference: post.externalReference,
        programmeExternalReference,
        onSuccess
      })
    );
  };

  return (
    <TribePoll
      poll={poll}
      createReaction={createReaction}
      deleteReaction={deleteReaction}
      switchReaction={switchReaction}
      pollResults={pollResults}
      retrieveResults={retrieveResults}
      post
    />
  );
};

export default TribePollContainer;
